import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import anime from 'animejs/lib/anime.es.js';
import { ChevronLeft, Instagram, ExternalLink } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useLanguage } from '../contexts/LanguageContext';

const TEX_EVENT = 'https://customer-assets.emergentagent.com/job_ac09e30e-ff84-4be5-9a8e-38b36c255d06/artifacts/dnyeucox_Wallpaper%20by%20%40koshiera.jpg';

// Metro Club event poster
const METRO_POSTER = 'https://customer-assets.emergentagent.com/job_7ae2fa0d-fc89-4b84-8ee2-77e1b09b9f84/artifacts/7htmggsp_VVSxMetroClub_Affiche_A3_Urban.png';

// Lineup with Instagram handles (all provided)
const LINEUP = [
  { name: 'BORED BOUDDHA', instagram: 'bored.bouddha_' },
  { name: 'FROMNOW', instagram: 'fromnowmusic' },
  { name: 'REDD', instagram: 'reddwith2d' },
  { name: 'UNSUNG', instagram: 'unsungroove_' },
  { name: 'RVRE', instagram: 'rvrebeats' },
  { name: 'MATOZ', instagram: 'matmatoz2' },
  { name: 'GASPICY', instagram: 'gaspicyy' },
  { name: 'FLYNN', instagram: 'flynn_spersaw' },
  { name: 'HUJI', instagram: 'hujigramm' },
  { name: 'LESD', instagram: 'lesd.music' },
  { name: 'BELLEBICHE', instagram: 'bellebiche98' },
  { name: 'LEUKOS', instagram: 'iamleukos' },
  { name: 'SOSA RISSA', instagram: 'sosa_rissa' },
  { name: 'ANTWAN', instagram: 'antwan.jpg' },
  { name: 'SLIMPROD', instagram: 'slimprod.ogs' },
  { name: 'HOLINESS', instagram: '__holiness' },
  { name: 'DI GELATO', instagram: 'donzinzin7080' },
  { name: 'SABER', instagram: 'saber.rio13' },
  { name: 'BORU', instagram: 'b0ruskii' },
  { name: 'BURNS', instagram: 'kaptn_burns' },
  { name: 'SONOFSATAN', instagram: 'sonofsxtxn' },
  { name: 'RAVYOL', instagram: 'ravyol_l' },
  { name: 'ASTRAL', instagram: 'hfrancis.1703' },
  { name: 'FELIX', instagram: 'felix.anciaux' },
  { name: 'LAURA CHARLIER', instagram: 'lauracharlier_' },
];

export default function EventMetroClub() {
  const { t } = useLanguage();
  const heroRef = useRef(null);
  const storyRef = useScrollReveal({ delay: 0 });
  const lineupRef = useScrollReveal({ delay: 0 });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    document.title = 'Metro Club × VVS // 27.06.2025';
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced || !heroRef.current) return;

    anime.timeline({ easing: 'easeOutExpo' })
      .add({ targets: '.emc-back', translateX: [-20, 0], opacity: [0, 1], duration: 600 })
      .add({ targets: '.emc-caption', translateY: [20, 0], opacity: [0, 1], duration: 600 }, '-=300')
      .add({ targets: '.emc-title', translateY: [60, 0], opacity: [0, 1], duration: 1000 }, '-=400')
      .add({ targets: '.emc-meta', translateY: [20, 0], opacity: [0, 1], duration: 600 }, '-=600')
      .add({ targets: '.emc-cta', scale: [0.95, 1], opacity: [0, 1], duration: 500 }, '-=300');
  }, []);

  useEffect(() => {
    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) return;
    
    setTimeout(() => {
      anime({
        targets: '.lineup-artist',
        translateY: [16, 0],
        opacity: [0, 1],
        duration: 500,
        delay: anime.stagger(25),
        easing: 'easeOutExpo',
      });
    }, 800);
  }, []);

  return (
    <div style={{ position: 'relative', zIndex: 1, paddingTop: '64px', overflowX: 'hidden' }}>

      {/* Hero */}
      <section ref={heroRef} style={{ 
        minHeight: isMobile ? '60vh' : '70vh', 
        position: 'relative', 
        display: 'flex', alignItems: 'flex-end', overflow: 'hidden' 
      }} data-testid="metro-club-hero">
        <img src={TEX_EVENT} alt="" style={{ 
          position: 'absolute', inset: 0, width: '100%', height: '100%', 
          objectFit: 'cover', opacity: 0.2 
        }} />
        <div style={{ 
          position: 'absolute', inset: 0, 
          background: 'linear-gradient(145deg, rgba(0,40,100,0.2) 0%, transparent 50%, rgba(5,10,20,0.8) 100%)' 
        }} />
        <div style={{ 
          position: 'absolute', bottom: 0, left: 0, right: 0, height: '60%', 
          background: 'linear-gradient(to top, #050A14 0%, transparent 100%)' 
        }} />

        <div style={{ 
          position: 'relative', 
          padding: isMobile ? '40px 20px' : '80px 48px', 
          width: '100%', 
          maxWidth: '1200px' 
        }}>
          <Link to="/events" className="emc-back" data-testid="metro-club-back"
            style={{ 
              display: 'inline-flex', alignItems: 'center', gap: '8px', 
              color: 'var(--vvs-muted)', textDecoration: 'none', 
              fontSize: '11px', letterSpacing: '0.15em', textTransform: 'uppercase', 
              fontFamily: 'Space Grotesk', marginBottom: isMobile ? '20px' : '32px', opacity: 0, 
              transition: 'color 0.3s',
              minHeight: '44px',
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#F0F4F8'}
            onMouseLeave={e => e.currentTarget.style.color = 'var(--vvs-muted)'}
          >
            <ChevronLeft size={14} /> Past Events
          </Link>

          <p className="emc-caption caption" style={{ marginBottom: '16px', color: 'var(--vvs-cyan)', opacity: 0 }}>
            27 June 2025
          </p>

          <h1 className="emc-title" style={{
            fontFamily: 'Syne, sans-serif', fontWeight: 800,
            fontSize: isMobile ? '36px' : 'clamp(48px, 10vw, 120px)',
            lineHeight: 0.85, letterSpacing: '-0.03em', textTransform: 'uppercase',
            color: '#F0F4F8', margin: '0 0 28px', opacity: 0,
          }}>
            Metro Club<br />× VVS
          </h1>

          <div className="emc-meta" style={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            flexWrap: 'wrap', 
            gap: isMobile ? '16px' : '40px', 
            alignItems: isMobile ? 'flex-start' : 'baseline', 
            marginBottom: '36px', opacity: 0 
          }}>
            <div>
              <p className="caption" style={{ marginBottom: '6px' }}>Venue</p>
              <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '16px' : '18px', color: 'var(--vvs-steel)' }}>
                Metro Club
              </p>
              <p style={{ fontFamily: 'Space Grotesk', fontSize: '13px', color: 'var(--vvs-muted)' }}>
                Ath, Belgium
              </p>
            </div>
            <div>
              <p className="caption" style={{ marginBottom: '6px' }}>Doors</p>
              <p style={{ fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: isMobile ? '20px' : '22px', color: '#F0F4F8' }}>
                22:00
              </p>
            </div>
            <div>
              <p className="caption" style={{ marginBottom: '6px' }}>Rooms</p>
              <p style={{ fontFamily: 'Space Grotesk', fontSize: isMobile ? '14px' : '16px', color: 'var(--vvs-steel)' }}>
                2 stages · 25 artists
              </p>
            </div>
          </div>

          <div className="emc-cta" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', opacity: 0 }}>
            <a href="https://vague-vision-sound.fairtix.io/p/metro-club-2025" target="_blank" rel="noopener noreferrer"
              className="btn-metallic btn-metallic-filled" data-testid="metro-club-tickets"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              Get Tickets <ExternalLink size={12} />
            </a>
            <a href="https://www.instagram.com/vaguevisionsound/" target="_blank" rel="noopener noreferrer"
              className="btn-metallic" data-testid="metro-club-instagram"
              style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <Instagram size={13} /> @vaguevisionsound
            </a>
          </div>
        </div>
      </section>

      {/* Event Poster */}
      <section style={{ padding: isMobile ? '40px 20px' : '60px 48px', borderTop: '1px solid rgba(240,244,248,0.05)' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <img 
            src={METRO_POSTER}
            alt="VVS × Metro Club - 27 June 2025"
            style={{
              width: '100%',
              maxWidth: isMobile ? '300px' : '450px',
              height: 'auto',
              borderRadius: '8px',
              boxShadow: '0 20px 60px rgba(0,50,100,0.3), 0 10px 30px rgba(0,0,0,0.5)',
            }}
            data-testid="metro-club-poster"
          />
        </div>
      </section>

      {/* Live Set Video 1 - UNSUNG B2B REDD (Most recent) */}
      <section style={{ padding: isMobile ? '40px 20px' : '60px 48px', borderTop: '1px solid rgba(240,244,248,0.05)' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: '11px',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: 'var(--vvs-cyan)',
            marginBottom: '12px',
            textAlign: 'center',
          }}>
            LIVE SET
          </p>
          <h2 style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 700,
            fontSize: isMobile ? '24px' : '32px',
            textTransform: 'uppercase',
            color: '#F0F4F8',
            marginBottom: '28px',
            textAlign: 'center',
          }}>
            UNSUNG B2B REDD
          </h2>

          {/* YouTube Embed - Responsive 16:9 */}
          <div style={{
            position: 'relative',
            width: '100%',
            paddingBottom: '56.25%',
            background: '#000',
            borderRadius: '4px',
            overflow: 'hidden',
          }}>
            <iframe
              src="https://www.youtube.com/embed/tWDtl5MEFmE?rel=0&modestbranding=1"
              title="UNSUNG B2B REDD // VVS x Metro Club"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </div>
        </div>
      </section>

      {/* Live Set Video 2 - Slimprod */}
      <section style={{ padding: isMobile ? '40px 20px' : '60px 48px', borderTop: '1px solid rgba(240,244,248,0.05)' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: '11px',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: 'var(--vvs-cyan)',
            marginBottom: '12px',
            textAlign: 'center',
          }}>
            LIVE SET
          </p>
          <h2 style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 700,
            fontSize: isMobile ? '24px' : '32px',
            textTransform: 'uppercase',
            color: '#F0F4F8',
            marginBottom: '28px',
            textAlign: 'center',
          }}>
            SLIMPROD B2B HOLINESS
          </h2>

          {/* YouTube Embed - Responsive 16:9 */}
          <div style={{
            position: 'relative',
            width: '100%',
            paddingBottom: '56.25%',
            background: '#000',
            borderRadius: '4px',
            overflow: 'hidden',
          }}>
            <iframe
              src="https://www.youtube.com/embed/VrdQ__hbAx8?rel=0&modestbranding=1"
              title="SLIMPROD B2B HOLINESS // VVS x Metro Club"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </div>
        </div>
      </section>

      {/* Live Set Video 2 - Axial Collective */}
      <section style={{ padding: isMobile ? '40px 20px' : '60px 48px', borderTop: '1px solid rgba(240,244,248,0.05)' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: '11px',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: 'var(--vvs-cyan)',
            marginBottom: '12px',
            textAlign: 'center',
          }}>
            LIVE SET
          </p>
          <h2 style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 700,
            fontSize: isMobile ? '24px' : '32px',
            textTransform: 'uppercase',
            color: '#F0F4F8',
            marginBottom: '12px',
            textAlign: 'center',
          }}>
            FLYNN B2B HUJI
          </h2>
          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: '13px',
            letterSpacing: '0.08em',
            color: 'var(--vvs-muted)',
            marginBottom: '28px',
            textAlign: 'center',
          }}>
            Axial Collective
          </p>

          {/* YouTube Embed - Responsive 16:9 */}
          <div style={{
            position: 'relative',
            width: '100%',
            paddingBottom: '56.25%',
            background: '#000',
            borderRadius: '4px',
            overflow: 'hidden',
          }}>
            <iframe
              src="https://www.youtube.com/embed/uQRFH2R8k0s?rel=0&modestbranding=1"
              title="FLYNN B2B HUJI // Axial Collective // VVS x Metro Club"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </div>
        </div>
      </section>

      {/* Live Set Video 3 - Bellebiche */}
      <section style={{ padding: isMobile ? '40px 20px' : '60px 48px', borderTop: '1px solid rgba(240,244,248,0.05)' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <p style={{
            fontFamily: 'Space Grotesk, sans-serif',
            fontSize: '11px',
            letterSpacing: '0.15em',
            textTransform: 'uppercase',
            color: 'var(--vvs-cyan)',
            marginBottom: '12px',
            textAlign: 'center',
          }}>
            LIVE SET
          </p>
          <h2 style={{
            fontFamily: 'Syne, sans-serif',
            fontWeight: 700,
            fontSize: isMobile ? '24px' : '32px',
            textTransform: 'uppercase',
            color: '#F0F4F8',
            marginBottom: '28px',
            textAlign: 'center',
          }}>
            BELLEBICHE B2B ANTWAN
          </h2>

          {/* YouTube Embed - Responsive 16:9 */}
          <div style={{
            position: 'relative',
            width: '100%',
            paddingBottom: '56.25%',
            background: '#000',
            borderRadius: '4px',
            overflow: 'hidden',
          }}>
            <iframe
              src="https://www.youtube.com/embed/Mb2JC7rrOZM?rel=0&modestbranding=1"
              title="BELLEBICHE B2B ANTWAN // VVS x Metro Club"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 'none',
              }}
            />
          </div>
        </div>
      </section>

      {/* The Gathering Story */}
      <section ref={storyRef} style={{ padding: isMobile ? '40px 20px' : '60px 48px', borderTop: '1px solid rgba(240,244,248,0.05)' }}>
        <div className="reveal-el" style={{ maxWidth: '700px', margin: '0 auto' }}>
          <p style={{ 
            fontFamily: 'Space Grotesk', fontSize: isMobile ? '16px' : 'clamp(16px, 2vw, 20px)', 
            lineHeight: 1.9, color: 'var(--vvs-steel)', textAlign: 'center',
            marginBottom: '20px',
          }}>
            Metro Club × Vague Vision Sound was built as a gathering point.
          </p>
          <p style={{ 
            fontFamily: 'Space Grotesk', fontSize: isMobile ? '14px' : 'clamp(14px, 1.8vw, 17px)', 
            lineHeight: 1.9, color: 'var(--vvs-muted)', textAlign: 'center',
          }}>
            A first step to bring the scene into one room, so artists could meet, connect and align before the next chapter.
          </p>
          <p style={{ 
            fontFamily: 'Space Grotesk', fontSize: isMobile ? '15px' : 'clamp(15px, 2vw, 18px)', 
            lineHeight: 1.9, color: 'var(--vvs-cyan)', textAlign: 'center', marginTop: '28px',
          }}>
            Every artist is equal. Every set matters.
          </p>
        </div>
      </section>

      {/* Full Lineup with Instagram links */}
      <section style={{ padding: isMobile ? '40px 20px 80px' : '60px 48px 100px', borderTop: '1px solid rgba(240,244,248,0.05)' }}>
        <div ref={lineupRef} className="reveal-el" style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <p className="caption" style={{ marginBottom: '36px' }}>Full Line-up // 25 Artists</p>
          
          <div data-testid="metro-club-lineup" style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '2px',
            borderTop: '1px solid rgba(240,244,248,0.05)',
          }}>
            {LINEUP.map((artist, i) => (
              artist.instagram ? (
                <a 
                  key={i} 
                  href={`https://instagram.com/${artist.instagram}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="lineup-artist" 
                  data-testid={`lineup-${artist.name.toLowerCase().replace(/\s+/g, '-')}`}
                  style={{
                    padding: isMobile ? '16px 12px' : '20px 18px',
                    borderBottom: '1px solid rgba(240,244,248,0.05)',
                    borderRight: isMobile ? 'none' : '1px solid rgba(240,244,248,0.05)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    opacity: 0,
                    textDecoration: 'none',
                    background: 'transparent',
                    transition: 'background 0.3s',
                    minHeight: '52px',
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,240,255,0.03)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{
                    fontFamily: 'Syne, sans-serif', fontWeight: 700,
                    fontSize: isMobile ? '14px' : 'clamp(13px, 1.6vw, 16px)',
                    letterSpacing: '0.04em', textTransform: 'uppercase',
                    color: artist.name === 'FROMNOW' || artist.name === 'RVRE' ? 'var(--vvs-cyan)' : '#F0F4F8',
                  }}>
                    {artist.name}
                  </span>
                  
                  <Instagram 
                    size={14} 
                    style={{ 
                      color: 'var(--vvs-cyan)', 
                      opacity: 0.7,
                    }} 
                  />
                </a>
              ) : (
                <div 
                  key={i} 
                  className="lineup-artist" 
                  data-testid={`lineup-${artist.name.toLowerCase().replace(/\s+/g, '-')}`}
                  style={{
                    padding: isMobile ? '16px 12px' : '20px 18px',
                    borderBottom: '1px solid rgba(240,244,248,0.05)',
                    borderRight: isMobile ? 'none' : '1px solid rgba(240,244,248,0.05)',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    opacity: 0,
                    background: 'transparent',
                    minHeight: '52px',
                  }}
                >
                  <span style={{
                    fontFamily: 'Syne, sans-serif', fontWeight: 700,
                    fontSize: isMobile ? '14px' : 'clamp(13px, 1.6vw, 16px)',
                    letterSpacing: '0.04em', textTransform: 'uppercase',
                    color: 'var(--vvs-muted)',
                    opacity: 0.5,
                  }}>
                    {artist.name}
                  </span>
                </div>
              )
            ))}
          </div>
        </div>
      </section>

      {/* Back to events */}
      <section style={{ padding: isMobile ? '0 20px 60px' : '0 48px 80px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
          <Link to="/events" className="btn-metallic" style={{ textDecoration: 'none', display: 'inline-block' }}>
            ← Back to Events
          </Link>
        </div>
      </section>

    </div>
  );
}
